/**
 * v3.3.1 — Translation Health strip.
 *
 * Compact, unobtrusive operational visibility at the top of the Surveys page.
 * Fetches /api/surveys/_translation_metrics on mount and every 30s.
 * Fails gracefully (shows "unavailable" — never blocks UI).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { surveysAPI } from '../api';

export function TranslationHealthStrip() {
    const { t } = useTranslation();
    const [metrics, setMetrics] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const data = await surveysAPI.translationMetrics();
                if (cancelled) return;
                setMetrics(data);
                setError(false);
            } catch {
                if (cancelled) return;
                setError(true);
            }
        };
        load();
        const timer = setInterval(load, 30000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, []);

    if (error) {
        return (
            <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-slate-100 dark:bg-slate-800 text-muted-foreground border border-slate-200 dark:border-slate-700"
                data-testid="translation-health-strip"
            >
                <Languages className="w-3.5 h-3.5 opacity-60" />
                <span>{t('surveys.translationHealthUnavailable', 'Translation Health unavailable')}</span>
            </div>
        );
    }

    if (!metrics) {
        return null; // render nothing on initial load; strip appears once data arrives
    }

    const total = metrics.translation_jobs_total ?? 0;
    const success = metrics.jobs_success ?? 0;
    const pending = metrics.jobs_pending ?? 0;
    const avg = metrics.avg_duration_seconds ?? 0;

    return (
        <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
            data-testid="translation-health-strip"
            title={t('surveys.translationHealthTooltip', 'Translation system health — refreshes every 30 seconds')}
        >
            <Languages className="w-3.5 h-3.5 text-fuchsia-600 dark:text-fuchsia-400" />
            <span className="font-medium text-slate-700 dark:text-slate-200">
                {t('surveys.translationHealth', 'Translation Health')}
            </span>
            <span className="opacity-70">·</span>
            <span data-testid="health-succeeded">
                {t('surveys.translationHealthSucceeded', '{{success}} / {{total}} succeeded', { success, total })}
            </span>
            <span className="opacity-70">·</span>
            <span data-testid="health-avg">
                {t('surveys.translationHealthAvg', 'avg {{avg}}s', { avg })}
            </span>
            <span className="opacity-70">·</span>
            <span data-testid="health-pending" className={pending > 0 ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                {t('surveys.translationHealthPending', '{{pending}} pending', { pending })}
            </span>
        </div>
    );
}

export default TranslationHealthStrip;
